import { useMemo, useState } from 'react';
import { DatePeriodSelect, PageHeader } from '@/components/molecules';
import {
  BillingTable,
  ReceiptDetailSheet,
  type BillingHistoryItem as LocalBillingItem,
} from './components';
import { Button, PageLoader } from '@/components/atoms';
import { getRangeFromPeriod } from '@/lib/helpers/format-date';
import { ArrowLeft } from 'lucide-react';
import { routes } from '@/lib/constants';
import { useNavigate } from 'react-router';
import { useBillingHistory } from '@/api';
import type { BillingHistoryItem } from '@/types';

const formatCurrency = (amount: string, currency: string) => {
  const symbol = currency === 'NGN' ? '₦' : '$';
  return `${symbol} ${parseFloat(amount).toLocaleString()}`;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const mapStatus = (
  status: BillingHistoryItem['status']
): LocalBillingItem['status'] => {
  if (status === 'paid') return 'Paid';
  if (status === 'pending') return 'Pending';
  return 'Failed';
};

const formatPaymentProvider = (
  provider: BillingHistoryItem['paymentProvider']
) => {
  if (provider === 'paystack') return 'Paystack';
  if (provider === 'stripe') return 'Stripe';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
};

const transformBillingData = (
  items: BillingHistoryItem[]
): LocalBillingItem[] => {
  return items.map((item) => ({
    id: item.invoiceId,
    invoiceId: item.invoiceId,
    plan: item.plan,
    amount: formatCurrency(item.amount, item.currency),
    rawAmount: item.amount,
    currency: item.currency,
    paymentMethod: formatPaymentProvider(item.paymentProvider),
    status: mapStatus(item.status),
    startDate: formatDate(item.startDate),
    endDate: formatDate(item.endDate),
    rawStartDate: item.startDate,
    rawEndDate: item.endDate,
    autoRenew: item.autoRenew ? 'On' : 'Off',
    paidAt: item.paidAt,
    receiptUrl: item.receiptUrl,
  }));
};

export const BillingHistoryPage = () => {
  const [period, setPeriod] = useState<string>('30days');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] =
    useState<LocalBillingItem | null>(null);
  const [page] = useState(1);

  const navigate = useNavigate();
  const { data: billingData, isLoading } = useBillingHistory(page);

  const filteredBillingHistory = useMemo(() => {
    if (!billingData?.data) return [];

    const { startDate, endDate } = getRangeFromPeriod(period);
    const rangeStart = new Date(startDate);
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);

    const filtered = billingData.data.filter((item) => {
      const dateValue = item.paidAt ?? item.startDate;
      if (!dateValue) return false;
      const itemDate = new Date(dateValue);
      return itemDate >= rangeStart && itemDate <= rangeEnd;
    });

    return transformBillingData(filtered);
  }, [billingData, period]);

  const handleViewReceipt = (billing: LocalBillingItem) => {
    setSelectedBilling(billing);
    setSheetOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        heading="Account settings"
        description="Manage your account settings and preferences"
      />

      <div className="space-y-6">
        <div className="flex justify-between">
          <Button
            variant="link"
            onClick={() => navigate(`${routes.settings}?tab=subscription`)}
          >
            <ArrowLeft /> Back
          </Button>
          <DatePeriodSelect
            value={period}
            onValueChange={setPeriod}
            triggerClassName="w-fit"
          />
        </div>

        {isLoading ? (
          <PageLoader message="Loading billing history..." className="py-20" />
        ) : (
          <BillingTable
            data={filteredBillingHistory}
            onView={handleViewReceipt}
          />
        )}
      </div>

      <ReceiptDetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        billingItem={selectedBilling}
      />
    </div>
  );
};
